import { parse } from '@jupiterone/query-language-parser';
import { useEffect } from 'react';
import { QueryV1Input } from '../../../../types/J1QL';
import useJ1QLSortingPaginationQuery from '../../../hooks/graphql/useJ1QLSortingPaginationQuery';
import { QueryResultsChange, QueryResultsChangeType } from '../../../types';
import {
  useMockHook,
  mockFunction,
  mockAsyncFunction,
} from '@jupiterone/jupiter-web-toolkit/src/util/useMockHook';

export interface HookProps {
  query: QueryV1Input;
  standardId?: string;
  onLoaded?: VoidFunction;
}

const useSortablePaginationQueryResultLogic = ({
  query,
  standardId,
  onLoaded,
}: HookProps) => {
  let queryError;
  try {
    if (!parse(query.query)) throw new Error('Unable to parse J1QL query');
  } catch (error) {
    queryError = error;
  }

  const {
    loading,
    result,
    removeResultById,
    refetch: returnedRefetch,
    loadMoreOrderBy,
    loadMore,
    totalResultCount,
  } = useJ1QLSortingPaginationQuery(query, standardId, Boolean(queryError));

  useEffect(() => {
    if (!loading) {
      onLoaded?.();
    }
  }, [loading]);

  const handleResultsChange = (changes: QueryResultsChange[]) => {
    if (changes.length > 1) {
      throw new Error('Multiple result changes not currently handled');
    }

    const [{ data: selectedRowID, type }] = changes;

    if (type === QueryResultsChangeType.Delete) {
      removeResultById(selectedRowID);
    }
  };

  // The interface of this hook (and all dependent child components) expects refetch
  // to be of type () => Promise<void>.  Until we can fix those typings, just re-create
  // a function of that type here
  const refetch = async () => {
    await returnedRefetch();
  };

  return {
    queryError,
    loading,
    result,
    refetch,
    loadMore,
    loadMoreOrderBy,
    handleResultsChange,
    totalResultCount,
  };
};

const mockData = {
  queryError: undefined,
  loading: false as any,
  result: null as any,
  handleResultsChange: mockAsyncFunction,
  refetch: mockAsyncFunction,
  loadMoreOrderBy: mockFunction,
  loadMore: mockFunction,
  totalResultCount: 10,
};

export default useMockHook(useSortablePaginationQueryResultLogic, mockData);
